import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { Row, Col, Form } from 'react-bootstrap5';
import useLoadTopics from 'components/new_ui/Hooks/useLoadTopics';
import SessionCountItem from './Widgets/SessionCountItem';
import SelectedTopic from './Widgets/SelectedTopic';

import StepSession from './Widgets/StepSession';
import StepStatic from './Widgets/StepStatic';

export default function Program({
  enterpriseId, selectedTopics, templateAttribute, onChange,
}) {
  const { topics: topicsList } = useLoadTopics({ enterprise_id: enterpriseId });

  const [programName, setProgramName] = useState(templateAttribute.title || '');
  const [countSessions, setCountSessions] = useState(templateAttribute.sessions || 6);
  const [sessionsTopics, setSessionsTopics] = useState(templateAttribute.steps || []);

  const [employeeChoiceTopic, setEmployeeChoiceTopic] = useState(null);

  const onDragEnd = ({ source, destination }) => {
    const topic = source.index === 0 ? employeeChoiceTopic : selectedTopics[source.index - 1];
    if (destination) {
      const [, sessionPosition] = destination.droppableId.split('_');
      const position = parseInt(sessionPosition, 10);
      const topics = sessionsTopics.filter((item) => item.position !== position);
      setSessionsTopics([...topics, { position, topic }]);
    }
  };

  const onTopicRemove = (position) => {
    const topics = sessionsTopics.filter((item) => item.position !== position);
    setSessionsTopics([...topics]);
  };

  const onChangeProgramName = (value) => {
    setProgramName(value);
  };

  const onChangeCountSessions = (value) => {
    setCountSessions(value);
  };

  useEffect(() => {
    onChange({
      programName,
      countSessions,
      sessionsTopics,
    });
  }, [programName, countSessions, sessionsTopics]);

  useEffect(() => {
    const topic = topicsList.find((item) => item.attributes.special_type === 'employee_choice');
    if (topic) {
      setEmployeeChoiceTopic(topic);
    }
  }, [topicsList]);

  return (
    <>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Program Name</Form.Label>

            <Form.Control
              value={programName}
              placeholder="Program Name"
              onChange={(e) => onChangeProgramName(e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Number of sessions</Form.Label>

            <div className="d-flex d-sm-block">
              <SessionCountItem
                key="count-session-3"
                countSessions={3}
                onClick={onChangeCountSessions}
                isActive={countSessions === 3}
              />

              <SessionCountItem
                key="count-session-6"
                countSessions={6}
                onClick={onChangeCountSessions}
                isActive={countSessions === 6}
              />

              <SessionCountItem
                key="count-session-12"
                countSessions={12}
                onClick={onChangeCountSessions}
                isActive={countSessions === 12}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      <DragDropContext onDragEnd={onDragEnd}>
        <Row className="mt-5">
          <Col md={6}>
            <div className="label mb-3">Selected Topics</div>

            <Droppable key="source" droppableId="source" isDropDisabled>
              {(provided) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <div {...provided.droppableProps} ref={provided.innerRef}>

                  {employeeChoiceTopic != null && (
                  <SelectedTopic
                    key="topic-employee-choice"
                    topic={employeeChoiceTopic}
                    index={0}
                  />
                  )}

                  {selectedTopics && selectedTopics
                    .filter((topic) => topic.attributes.special_type !== 'employee_choice')
                    .map((topic, index) => (
                      <SelectedTopic
                        key={`topic-${topic.id}`}
                        topic={topic}
                        index={index + 1}
                      />
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </Col>

          <Col md={6}>
            <div className="label mb-3">Your Program</div>

            <div className="selected-topics-wrapper counting-icons-wrapper">
              <StepStatic
                key="step-first-cpa"
                title="First Talent Assessment"
                iconClass="career-pulse-assessment-icon"
              />

              <StepSession
                key="session-step-1"
                position={1}
                assignTopic={sessionsTopics.find((item) => item.position === 1)?.topic}
                onTopicRemove={onTopicRemove}
              />

              <StepStatic key="step-coach-feedback" title="Coach Feedback" iconClass="star-filled-icon" />

              {Array.from(Array(countSessions - 1)).map((_x, i) => (
                <React.Fragment key={`session-step-${i + 2}`}>
                  {countSessions !== 3 && i === countSessions - 2
                      && (
                      <StepStatic
                        title="Final Talent Assessment"
                        iconClass="career-pulse-assessment-icon"
                      />
                      )}
                  <StepSession
                    position={i + 2}
                    assignTopic={sessionsTopics.find((item) => item.position === i + 2)?.topic}
                    onTopicRemove={onTopicRemove}
                  />

                  {countSessions === 12 && i === 4 && (
                    <StepStatic
                      title="Mid-Program Talent Assessment"
                      iconClass="career-pulse-assessment-icon"
                    />
                  )}
                </React.Fragment>
              ))}

              {countSessions === 3
                  && (
                  <StepStatic
                    key="step-final-cpa-2"
                    title="Final Talent Assessment"
                    iconClass="career-pulse-assessment-icon"
                  />
                  )}

              <StepStatic key="step-program-feedback" title="Program End Survey" iconClass="star-filled-icon" />
            </div>
          </Col>
        </Row>
      </DragDropContext>
    </>
  );
}

Program.propTypes = {
  enterpriseId: PropTypes.number.isRequired,
  selectedTopics: PropTypes.arrayOf(PropTypes.object),
  templateAttribute: PropTypes.object,
  onChange: PropTypes.func,
};

Program.defaultProps = {
  selectedTopics: null,
  templateAttribute: null,
  onChange: () => {},
};
